/**
 * Metrics are a **list** at every level that carries one: the enterprise's company metrics, a team
 * charter's metrics, and a task version's metrics.
 *
 * #372 shipped HQ visibility for a single "one metric" per team. The answer back was that one number per
 * level was never enough — a team measures more than one thing, a task does too, and the company has
 * numbers of its own that belong to no single team. So every level moved from one metric to many.
 *
 * The list lives where the single metric already lived: free text, **one metric per line**. Each of those
 * fields (`charter.metric`, a task version's `metric.text`, and the enterprise's new `metrics`) was already
 * a multi-line text field, so a row written before this change parses as exactly one metric and needs no
 * backfill, and a row written after it holds as many as someone typed. Read through `metricList` and write
 * through `metricsText`, and nothing above this has to know which of the two it is holding.
 */

/** Leading bullet on a pasted list (`- `, `* `, `• `), which is punctuation, not part of the metric. */
const BULLET = /^\s*[-*•]\s+/

/** The `1. ` / `2) ` opening of a numbered list — or the first word of a label like `2024. Revenue target`. */
const NUMBERED = /^\s*(\d+)[.)]\s+/

/**
 * Whether the `1. ` openings in a block are list numbering rather than text someone meant to keep. A metric
 * label may legitimately start with a number — `2024. Revenue target`, `100. Requests/sec cap`, `1) Escaped
 * defects` — and stripping that rewrites what was typed, silently and for good, because the enterprise
 * editor saves through `metricList` and stores through `metricsText`. So a number counts as a marker only
 * where the block around it reads as a pasted list: more than one line opens with a marker of some kind,
 * and the numbering starts at 1 the way a pasted list does.
 */
function numbersAreMarkers(lines: readonly string[]): boolean {
    const numbers = lines
        .map(line => NUMBERED.exec(line))
        .filter((match): match is RegExpExecArray => match !== null)
        .map(match => Number(match[1]))
    const markers = numbers.length + lines.filter(line => BULLET.test(line)).length
    return markers > 1 && numbers[0] === 1
}

/**
 * The metrics a stored field holds, in the order they were written: trimmed, de-bulleted, blanks dropped.
 * An empty or whitespace-only field is no metrics at all — which is exactly what Blockers reports as a
 * team with no metric, so a team that clears its last line is still flagged.
 */
export function metricList(value: string | null | undefined): string[] {
    if (typeof value !== 'string') {
        return []
    }
    const lines = value.split(/\r?\n/).filter(line => line.trim().length > 0)
    const numbered = numbersAreMarkers(lines)
    return lines
        .map(line => {
            const bare = line.replace(BULLET, '')
            return (numbered ? bare.replace(NUMBERED, '') : bare).trim()
        })
        .filter(line => line.length > 0)
}

/**
 * The stored form of a list of metrics. Accepts the text form as well, so a caller holding whatever came
 * over the wire (a `string[]` from the portal, a legacy `string` from an older client) can normalise it
 * without branching first.
 */
export function metricsText(value: readonly string[] | string | null | undefined): string {
    const entries = Array.isArray(value) ? value : [value]
    return entries.flatMap(entry => metricList(entry)).join('\n')
}

/** Whether anything at all is measured here. A blank field, whitespace, or an empty list is not. */
export function hasMetrics(value: string | null | undefined): boolean {
    return metricList(value).length > 0
}
